<template>
    <AppDialog
        v-model="isOpen"
        size="sm"
        append-to-body
        @close="$emit('close')"
    >
        <template #header>
            <h2 class="text-lg font-semibold text-theme-base">{{ title }}</h2>
        </template>

        <div v-if="prompt" class="space-y-3 text-sm text-theme-base">
            <template v-if="prompt.kind === 'limit'">
                <p v-if="!isNotIncluded">
                    Tu plan permite {{ prompt.limit }} {{ resourceLabel }} y ya
                    tienes {{ prompt.used }}.
                </p>
                <p v-if="choosePlan">
                    Elige un plan para tener
                    {{ isNotIncluded ? "" : "más" }} {{ resourceLabel }}.
                </p>
                <p v-else-if="plan">
                    Con el plan <strong>{{ plan.name }}</strong>
                    <span v-if="plan.price !== undefined">
                        ({{ plan.price | currency }} al mes)
                    </span>
                    puedes tener
                    <strong>{{ planLimitText }}</strong
                    >.
                </p>
                <p v-else class="text-theme-muted">
                    Ya estás en el plan con más {{ resourceLabel }}.
                </p>
            </template>

            <template v-else>
                <p v-if="featureItem">{{ featureItem.description }}</p>
                <p v-if="choosePlan">
                    Elige el plan que mejor se ajuste a tu negocio para
                    activarla.
                </p>
                <p v-else-if="plan">
                    Está incluida desde el plan
                    <strong>{{ plan.name }}</strong>
                    <span v-if="plan.price !== undefined">
                        ({{ plan.price | currency }} al mes)
                    </span>
                    .
                </p>
            </template>

            <plan-benefits-list
                v-if="benefitsPlanCode"
                class="pt-2"
                :plan-code="benefitsPlanCode"
                :title="benefitsTitle"
            />
        </div>

        <template #footer>
            <div class="flex flex-col sm:flex-row justify-end gap-2">
                <button
                    class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                    @click="isOpen = false"
                >
                    Ahora no
                </button>
                <app-button
                    v-if="choosePlan"
                    color="brand-blue"
                    @click="$emit('go-to-plans')"
                >
                    Ver planes
                </app-button>
                <app-button
                    v-else-if="plan"
                    color="brand-blue"
                    @click="$emit('go-to-plan', plan)"
                >
                    Ver plan {{ plan.name }}
                </app-button>
            </div>
        </template>
    </AppDialog>
</template>

<script>
import {
    findPlanFeature,
    findPlanResource,
    nextPlanCode,
    planNameFromCode
} from "@/common/fields/plan-features";

import AppDialog from "@/components/utils/AppDialog.vue";
import PlanBenefitsList from "@/components/utils/PlanBenefitsList.vue";

export default {
    name: "PlanUpgradeDialog",
    components: { AppDialog, PlanBenefitsList },
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        // { kind: "feature", feature } o
        // { kind: "limit", resourceCode, limit, used }.
        prompt: {
            type: Object,
            default: null
        },
        // Plan que lo desbloquea: { slug, code, name, price?, limit? } o null.
        plan: {
            type: Object,
            default: null
        },
        // Cuenta en Free: invita a elegir entre todos los planes en vez de
        // sugerir el más barato que lo desbloquea.
        choosePlan: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        isOpen: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        },
        featureItem() {
            return this.prompt && this.prompt.feature
                ? findPlanFeature(this.prompt.feature)
                : null;
        },
        // Tope 0: el plan no trae el recurso (ej. api keys en Free/Starter).
        isNotIncluded() {
            return (
                Boolean(this.prompt) &&
                this.prompt.kind === "limit" &&
                this.prompt.limit === 0
            );
        },
        resourceLabel() {
            const resource =
                this.prompt && findPlanResource(this.prompt.resourceCode);
            return resource ? resource.label : "";
        },
        planLimitText() {
            if (!this.plan) return "";
            return this.plan.limit === null
                ? `${this.resourceLabel} sin límite`
                : `hasta ${this.plan.limit} ${this.resourceLabel}`;
        },
        // Beneficios del plan que propone el modal; en Free sin plan puntual,
        // los del plan siguiente.
        benefitsPlanCode() {
            if (this.plan && this.plan.code) return this.plan.code;
            return this.choosePlan ? nextPlanCode("free") : null;
        },
        benefitsTitle() {
            const name =
                this.plan && this.plan.code === this.benefitsPlanCode
                    ? this.plan.name
                    : planNameFromCode(this.benefitsPlanCode);
            return `Con ${name} tienes:`;
        },
        title() {
            if (!this.prompt) return "";
            if (this.isNotIncluded) {
                return `Tu plan no incluye ${this.resourceLabel}`;
            }
            if (this.prompt.kind === "limit") {
                return `Llegaste al tope de ${this.resourceLabel}`;
            }
            const label = this.featureItem ? this.featureItem.label : "";
            if (this.choosePlan) return `${label}: elige un plan`;
            return this.plan ? `${label}: pásate a ${this.plan.name}` : label;
        }
    }
};
</script>
